import { isAxiosError } from 'axios'

import type { DomainPermission } from '@percent/admin-dashboard/context/auth/authContext/AuthContext.types'

type PermissionsSource = {
  getPermissions: () => Promise<{ data: { data: DomainPermission[] } }>
}

// Only a failed request: on a 401 the axios interceptor signs the admin out and resolves, so
// asking again would sign them out again
const retryable = (error: unknown) => isAxiosError(error) && (!error.response || error.response.status >= 500)

const wait = (ms: number) =>
  new Promise<void>(resolve => {
    setTimeout(resolve, ms)
  })

export const PERMISSIONS_RETRY_DELAYS_MS = [1_000, 3_000] as const

// The permissions decide what the nav and every page show, so one failed answer would leave the
// admin with an empty dashboard until they reload. pbp-api answers 500 when OpenFGA leaves some of
// the checks unanswered, which passes within seconds, so a server or network failure is asked again.
export const fetchPermissions = async (
  iamService: PermissionsSource,
  retryDelaysMs: readonly number[] = PERMISSIONS_RETRY_DELAYS_MS
): Promise<DomainPermission[]> => {
  try {
    const {
      data: { data: permissions }
    } = await iamService.getPermissions()

    return permissions
  } catch (error) {
    const [delay, ...laterDelays] = retryDelaysMs

    if (delay === undefined || !retryable(error)) throw error
    await wait(delay)

    return fetchPermissions(iamService, laterDelays)
  }
}
